import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { byNearest, isPast } from './search-order';
import { calendarQueryIds } from '../../lib/calendar-visibility';
import { Search } from '../../icons';
import { SearchInput } from '../../components/shared';
import { listEvents, type CalendarEventDto, type CalendarEventType, TYPE_COLOR_MAP } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { describeCalendarError, type FriendlyError } from './calendarErrors';
import { CalendarErrorNotice } from './CalendarErrorNotice';
import { TypeIcon } from './calendarTypeMeta';
import { getDateLocale } from '../../lib/base-i18n';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import './EventSearch.css';

/**
 * 일정 검색 — 캘린더 헤더의 검색 버튼 → 입력창 + 결과 드롭다운.
 *
 * 서버에 검색 라우트가 없어(제목·설명 필터 파라미터 자체가 없다) 새 API 를 만들지 않고
 * **기존** `listEvents(from, to)` 를 검색 전용으로 넓은 기간에 한 번 더 불러 클라이언트에서
 * title/description 부분일치로 거른다. 서버의 조회 기간 상한(MAX_RANGE_MS=365일)을 넘지
 * 않도록 오늘 기준 앞뒤 170일(총 340일)로 창을 고정한다 — 그보다 오래된/먼 일정은 못 찾는다는
 * 뜻이라, 결과가 없을 때 그 사실을 안내 문구로 밝힌다(조용히 "없음"으로만 보여주지 않는다).
 *
 * 반복 일정은 이 창 안에서 서버가 이미 회차별로 펼쳐 주므로(occurrence expand), 검색 결과의
 * 각 항목은 실제 날짜를 가진 구체적인 회차다 — "그 반복 일정 중 하나를 클릭하면 그 날짜로
 * 이동" 이 자연스럽게 된다.
 */

const SEARCH_WINDOW_PAST_DAYS = 170;
const SEARCH_WINDOW_FUTURE_DAYS = 170;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 30;

export interface EventSearchProps {
  /** 결과를 골랐을 때 — 호출부가 그 날짜로 이동 + 상세를 연다. */
  onSelect: (event: CalendarEventDto) => void;
  /** 2026-09-09 — 검색이 현재 필터를 존중하게. 체크된 캘린더(null=전체)·팀원 확장·그리드와 같은 클라이언트 술어. 전부 옵셔널(기존 호출 무영향). */
  calendarIds?: string[] | null;
  teamMembers?: boolean;
  filterFn?: (event: CalendarEventDto) => boolean;
}

export function EventSearch({ onSelect, calendarIds, teamMembers, filterFn }: EventSearchProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [results, setResults] = useState<CalendarEventDto[]>([]);
  // 필터 때문에 결과에서 빠진 건수 — "찾았는데 안 보인다"를 설명하려고 표시한다(2026-09-09).
  const [hiddenCount, setHiddenCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // 검색창을 열면 바로 입력할 수 있게.
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // 바깥 클릭 / Esc — DateTimePicker 와 동일한 패턴.
  useEscapeClose(() => setOpen(false), !open);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (boxRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  // 검색 창(고정) — "오늘" 이 아니라 컴포넌트가 열릴 때마다 재계산할 필요는 없다.
  // 하루 이틀 지나도 340일짜리 창이니 체감 오차가 없어 useMemo(deps: query 변경마다 재사용).
  const { from, to } = useMemo(() => {
    const f = new Date(); f.setDate(f.getDate() - SEARCH_WINDOW_PAST_DAYS); f.setHours(0, 0, 0, 0);
    const t = new Date(); t.setDate(t.getDate() + SEARCH_WINDOW_FUTURE_DAYS); t.setHours(0, 0, 0, 0);
    return { from: f.toISOString(), to: t.toISOString() };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setHiddenCount(0);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const abortController = new AbortController();
    const handle = setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      /*
        ⏸ 정본은 여기에 `{ teamMembers }` 를 다섯째 인자로 함께 넘긴다.
          · 까닭 — main 서버의 `listQuerySchema` 에 그 파라미터가 «없다»(from·to·calendarIds 뿐).
            보내도 ajv 가 조용히 지워서 아무 일이 안 일어난다.
          · 만료 조건 — 서버가 `teamMembers` 를 받으면.
          · 그때 할 일 — 이 줄에 그 인자를 되살리고, `listEvents` 에도 그 칸을 더한다(2판-b).
          ⇒ 그때까지 아래 `teamMembers` prop 은 «받아만» 두고 안 쓴다 — 정본의 꼴을 그대로 두려는 것이다.
      */
      // ⭐ 2026-09-30 — 그리드와 «같은 규칙»을 쓴다(lib/calendar-visibility). 따로 세면
      //   검색과 그리드가 «다른 일정»을 보인다.
      // ⭐ 2026-10-13 — 위 ⏸ 의 만료 조건이 찼다(서버가 teamMembers 를 받는다) ⇒ 정본대로 다섯째 인자로 넘긴다.
      listEvents(from, to, calendarQueryIds(calendarIds ?? null), abortController.signal, { teamMembers: !!teamMembers })
        .then((res) => {
          if (cancelled) return;
          const needle = q.toLowerCase();
          // 2026-08-24 — "사용자 이름으로도 검색할 수 있게" 리포트. 작성자·참석자(명시 RSVP)·
          // 팀 캘린더 구성원(팀 일정은 attendees 행이 없어 teamMembers 로 대신 채워진다 —
          // EventDetailModal 참석자 표시와 같은 폴백 규칙) 이름까지 포함한다.
          const matched = res.events.filter((e) => (
            e.title.toLowerCase().includes(needle)
            || (e.description ?? '').toLowerCase().includes(needle)
            || (e.location ?? '').toLowerCase().includes(needle)
            || (e.creator?.name ?? '').toLowerCase().includes(needle)
            || e.attendees.some((a) => (a.name ?? '').toLowerCase().includes(needle))
            || (e.teamMembers ?? []).some((m) => m.name.toLowerCase().includes(needle))
          ));
          /*
           * ⭐ 「가까운 날짜 우선」 — "지금 찾는 그 회의" 는 대개 가까운 날짜다.
           * ⛔ 2026-09-30 까지 이 줄은 `a.startAt.localeCompare(b.startAt)`(그냥 이른 것부터)였다.
           *   창이 ±170일이라 「170일 전」 것이 «맨 앞»에 서고, 상한이 `MAX_RESULTS = 30` 이라
           *   **30건을 넘으면 다가오는 일정이 영영 안 보였다.** 주석은 맞고 코드가 틀렸던 자리다.
           * ⇒ 판정은 `search-order.ts` 한 곳에 둔다 — 시각을 «넣어야» 시험이 「지금」을 못 박는다.
           * ⚠ 지난 것과 다가올 것이 «섞인다» — 그것이 이 차례의 뜻이다.
           *   ⇒ 그래서 줄에 「지남」 꼬리표를 «함께» 넣었다(아래 `isPast`). 차례만 바꾸고 표시를
           *     안 넣으면 «읽기가 나빠지는 쪽»만 생긴다 — 종전에는 지난 것이 앞에 몰려 있었다.
           */
          matched.sort(byNearest(Date.now()));
          const shown = filterFn ? matched.filter(filterFn) : matched;
          setHiddenCount(matched.length - shown.length);
          setResults(shown.slice(0, MAX_RESULTS));
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof Error && err.name === 'CanceledError') return;
          // 2026-09-15 — 원문(err.message) 대신 무엇이·왜·어떻게.
          setError(describeCalendarError(err, 'search'));
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
      abortController.abort();
    };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, from, to, calendarIds, teamMembers, filterFn]);

  function pick(e: CalendarEventDto) {
    onSelect(e);
    setOpen(false);
    setQuery('');
  }

  if (!open) {
    return (
      <button
        type="button"
        className="calendar-search-trigger"
        onClick={() => setOpen(true)}
        aria-label={tCalendar('calendar.search.placeholder')}
        title={tCalendar('calendar.search.placeholder')}
      >
        <Search size={14} />
      </button>
    );
  }

  return (
    <div className="calendar-search" ref={boxRef}>
      {/*
        ⭐ 2026-10-27 — 열린 입력칸을 공용 `SearchInput` 으로 옮겼다(사용자 승인 · 전후 화면 확인).
          · 왼쪽 돋보기 · 글자가 «있을 때만» 오른쪽 지우기 ✕ · Esc 는 «글자가 있으면» 비운다(공용 규칙).
          · 글자가 없을 때의 Esc 와 바깥 클릭은 종전대로 검색창을 «닫는다»(위 useEscapeClose · mousedown).
          · 찾는 중에는 지우기 자리에 공용 스피너가 돈다(`loading`) — 종전의 따로 그리던 Loader2 대신.
          ⚠ 종전 상자 끝의 «닫기 ✕» 는 없어졌다 — 지우기 ✕ 와 둘이 나란히 서면 무엇이 무엇인지 안 갈린다.
      */}
      <SearchInput
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onClear={() => { setQuery(''); inputRef.current?.focus(); }}
        clearLabel={tCalendar('calendar.search.clear')}
        placeholder={tCalendar('calendar.search.placeholder')}
        aria-label={tCalendar('calendar.search.placeholder')}
        size="sm"
        loading={loading}
        wrapperClassName="calendar-search-field"
        maxLength={200}
      />

      {query.trim() && (
        <div className="calendar-search-pop" role="listbox" aria-label={tCalendar('calendar.search.placeholder')}>
          {error && <CalendarErrorNotice className="calendar-search-msg" error={error} />}
          {!error && !loading && results.length === 0 && (
            <div className="calendar-search-msg">
              {tCalendar('calendar.search.empty')}
              {hiddenCount > 0 && <div className="calendar-search-hint">{tCalendar('calendar.search.hidden_by_filter', { n: hiddenCount })}</div>}
              <div className="calendar-search-hint">{tCalendar('calendar.search.hint_range')}</div>
            </div>
          )}
          {results.map((e) => {
            const meta = TYPE_COLOR_MAP[e.type as CalendarEventType] ?? { label: e.type, color: 'var(--text-muted)' };
            const past = isPast(e, Date.now());
            const d = new Date(e.startAt);
            const dateStr = d.toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric', weekday: 'short' });
            return (
              <button
                key={`${e.id}@${e.occurrenceAt ?? ''}`}
                type="button"
                role="option"
                aria-selected={false}
                className={`calendar-search-item${past ? ' calendar-search-item--past' : ''}`}
                onClick={() => pick(e)}
              >
                <span className="calendar-search-item-ico" style={{ color: meta.color }} aria-hidden>
                  <TypeIcon type={e.type} size={13} />
                </span>
                <span className="calendar-search-item-body">
                  <span className="calendar-search-item-title">{e.title}</span>
                  {e.location && <span className="calendar-search-item-loc">{e.location}</span>}
                </span>
                {/*
                  ⭐ 「지남」 꼬리표 (2026-09-30) — 차례가 「가까운 날짜 우선」이라 지난 것과
                    다가올 것이 «한 줄 걸러» 섞인다. 날짜만 적으면(「10월 3일 (금)」) 사람이
                    지난 회의를 다가올 것으로 읽는다.
                  ⚠ «흐리게»만으로는 색맹·고대비 설정에서 안 보인다 ⇒ «글자»를 함께 둔다.
                */}
                {past && <span className="calendar-search-item-past">{tCalendar('calendar.search.past_badge')}</span>}
                <span className="calendar-search-item-date">{dateStr}</span>
              </button>
            );
          })}
          {/* 결과가 있어도 필터로 빠진 게 있으면 맨 아래 한 줄 — "분명 있는데 왜 안 나오지" 방지(2026-09-09) */}
          {!error && !loading && results.length > 0 && hiddenCount > 0 && (
            <div className="calendar-search-msg calendar-search-hint">{tCalendar('calendar.search.hidden_by_filter', { n: hiddenCount })}</div>
          )}
        </div>
      )}
    </div>
  );
}
